import React, { useEffect, useMemo, useState } from 'react';
import type { StudentInfo } from '../types';
import { advisorApiService } from '../services/advisor-api';
import '../styles/StudentForm.css';

interface StudentFormProps {
  onSubmit: (studentInfo: StudentInfo) => Promise<void>;
  initialValues?: StudentInfo;
  onClose?: () => void;
}

type Combo = readonly [string, string, string];

/**
 * Fallback (studentType, school, program) combinations, used until the
 * backend list loads (or if the fetch fails). The live list is served from
 * GET /api/v1/cucs-advising/program_profiles, backed by the
 * cs_advisor_program_codes table, so program renames don't need a redeploy.
 */
const FALLBACK_COMBOS: readonly Combo[] = [
  // SEAS — Undergraduate (listed first so SEAS is the default school)
  ['Undergraduate', 'SEAS', 'COMS Major'],
  ['Undergraduate', 'SEAS', 'Computer Science Minor'],
  ['Undergraduate', 'SEAS', 'AI Minor'],
  // Barnard College — Undergraduate
  ['Undergraduate', 'Barnard College', 'COMS Major'],
  ['Undergraduate', 'Barnard College', 'CS/Math Major'],
  ['Undergraduate', 'Barnard College', 'Information Science Major'],
  ['Undergraduate', 'Barnard College', 'Data Science Major'],
  // CC — Undergraduate
  ['Undergraduate', 'CC', 'CS/Math Major'],
  ['Undergraduate', 'CC', 'COMS Major'],
  ['Undergraduate', 'CC', 'Data Science Major'],
  ['Undergraduate', 'CC', 'Information Science Major'],
  ['Undergraduate', 'CC', 'Computer Science Minor'],
  ['Undergraduate', 'CC', 'CS/Biology Major'],
  // GS — Undergraduate
  ['Undergraduate', 'GS', 'CS/Math Major'],
  ['Undergraduate', 'GS', 'COMS Major'],
  ['Undergraduate', 'GS', 'Data Science Major'],
  ['Undergraduate', 'GS', 'Information Science Major'],
  ['Undergraduate', 'GS', 'Computer Science Minor'],
  ['Undergraduate', 'GS', 'CS/Biology Major'],
  // SEAS — MS
  ['MS', 'SEAS', 'COMS MS (In-Person)'],
  ['MS', 'SEAS', 'COMS MS (Online)'],
  ['MS', 'SEAS', 'Journalism/CS Masters'],
  // CVN — MS
  ['MS', 'CVN', 'COMS MS (Online)'],
  // Journalism — MS
  ['MS', 'Journalism', 'Journalism/CS Masters'],
  // Bridge
  ['Bridge', 'SEAS', 'Bridge to MS'],
  // pre-MS (MS-to-PhD)
  ['pre-MS (MS-to-PhD)', 'SEAS', 'COMS Doctoral (MS/Phd)'],
  // PhD
  ['PhD', 'SEAS', 'COMS Doctoral (DES)'],
  ['PhD', 'SEAS', 'COMS Doctoral PhD'],
  ['PhD', 'GSAS', 'COMS Doctoral PhD'],
] as const;

function studentTypesOf(combos: readonly Combo[]): string[] {
  return [...new Set(combos.map(([t]) => t))];
}

function validSchoolsFor(combos: readonly Combo[], studentType: string): string[] {
  const lower = studentType.toLowerCase();
  return [...new Set(combos.filter(([t]) => t.toLowerCase() === lower).map(([, s]) => s))];
}

function validProgramsFor(combos: readonly Combo[], studentType: string, school: string): string[] {
  const lt = studentType.toLowerCase();
  const ls = school.toLowerCase();
  return [...new Set(
    combos
      .filter(([t, s]) => t.toLowerCase() === lt && s.toLowerCase() === ls)
      .map(([, , p]) => p),
  )];
}

interface Selection {
  studentType: string;
  school: string;
  program: string;
}

/**
 * Pick a valid (studentType, school, program) selection from `combos`,
 * preferring `wanted` values (in field order), falling back to the first
 * valid option for each field.
 */
function reconcileSelection(combos: readonly Combo[], wanted: Partial<Selection>): Selection {
  const types = studentTypesOf(combos);
  const studentType = types.includes(wanted.studentType ?? '') ? wanted.studentType! : types[0] ?? '';
  const schools = validSchoolsFor(combos, studentType);
  const school = schools.includes(wanted.school ?? '') ? wanted.school! : schools[0] ?? '';
  const programs = validProgramsFor(combos, studentType, school);
  const program = programs.includes(wanted.program ?? '') ? wanted.program! : programs[0] ?? '';
  return { studentType, school, program };
}

export const StudentForm: React.FC<StudentFormProps> = ({
  onSubmit,
  initialValues,
  onClose: _onClose,
}) => {
  const [combos, setCombos] = useState<readonly Combo[]>(FALLBACK_COMBOS);
  const [status, setStatus] = useState(initialValues?.status ?? 'Current');
  const [visaRestriction, setVisaRestriction] = useState(initialValues?.visa_restriction ?? 'No');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [selection, setSelection] = useState<Selection>(() =>
    reconcileSelection(FALLBACK_COMBOS, {
      studentType: initialValues?.student_type,
      school: initialValues?.school,
      program: initialValues?.program,
    })
  );
  const { studentType, school, program } = selection;

  // Load the live combinations from the backend; keep the fallback on error.
  useEffect(() => {
    let cancelled = false;
    advisorApiService
      .getProgramProfiles()
      .then((res) => {
        if (cancelled || !res.data?.profiles?.length) return;
        const fetched: Combo[] = res.data.profiles.map((p) => [
          p.student_type,
          p.school,
          p.program,
        ]);
        setCombos(fetched);

        // Pure functional update. Preference order per field: the saved
        // profile first (the fallback list may have wrongly rejected it in
        // the initializer — the fetched list is authoritative), then the
        // current selection, then the first valid option.
        setSelection((current) => {
          const types = studentTypesOf(fetched);
          const studentType =
            [initialValues?.student_type, current.studentType].find(
              (t): t is string => !!t && types.includes(t)
            ) ?? types[0] ?? '';
          const schools = validSchoolsFor(fetched, studentType);
          const school =
            [initialValues?.school, current.school].find(
              (s): s is string => !!s && schools.includes(s)
            ) ?? schools[0] ?? '';
          const programs = validProgramsFor(fetched, studentType, school);
          const program =
            [initialValues?.program, current.program].find(
              (p): p is string => !!p && programs.includes(p)
            ) ?? programs[0] ?? '';
          return { studentType, school, program };
        });
      })
      .catch(() => {
        // Fallback list stays active.
      });
    return () => {
      cancelled = true;
    };
  }, [initialValues]);

  const allStudentTypes = useMemo(() => studentTypesOf(combos), [combos]);
  const availableSchools = useMemo(() => validSchoolsFor(combos, studentType), [combos, studentType]);
  const availablePrograms = useMemo(
    () => validProgramsFor(combos, studentType, school),
    [combos, studentType, school]
  );

  const handleStudentTypeChange = (newType: string) => {
    setSelection((current) =>
      reconcileSelection(combos, { ...current, studentType: newType })
    );
  };

  const handleSchoolChange = (newSchool: string) => {
    setSelection((current) => reconcileSelection(combos, { ...current, school: newSchool }));
  };

  const handleProgramChange = (newProgram: string) => {
    setSelection((current) => ({ ...current, program: newProgram }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      await onSubmit({
        status,
        student_type: studentType,
        school,
        program,
        visa_restriction: visaRestriction,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit student info');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="setup-panel">
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="status">Status</label>
          <select id="status" value={status} onChange={(e) => setStatus(e.target.value)} required>
            <option value="Applicant">Applicant</option>
            <option value="Current">Current student</option>
            <option value="Alumni">Alumni</option>
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="student-type">Student Type</label>
          <select
            id="student-type"
            value={studentType}
            onChange={(e) => handleStudentTypeChange(e.target.value)}
            required
          >
            {allStudentTypes.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="school">School</label>
          <select
            id="school"
            value={school}
            onChange={(e) => handleSchoolChange(e.target.value)}
            required
          >
            {availableSchools.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="program">Program</label>
          <select
            id="program"
            value={program}
            onChange={(e) => handleProgramChange(e.target.value)}
            required
          >
            {availablePrograms.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="visa">Visa Restrictions</label>
          <select
            id="visa"
            value={visaRestriction}
            onChange={(e) => setVisaRestriction(e.target.value)}
            required
          >
            <option value="No">No</option>
            <option value="Yes - I have an F1 or J1 visa">Yes - I have an F1 or J1 visa</option>
          </select>
        </div>
        <div className="setup-actions">
          <button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Updating...' : 'Update Profile'}
          </button>
        </div>
        {error && <div className="setup-error">{error}</div>}
      </form>
    </div>
  );
};
